import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { TID } from "@/lib/testIds";
import DpiitBadge from "./DpiitBadge";
import ShimmerButton from "@/components/magicui/shimmer-button";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: {
      delay: 0.35 + i * 0.14,
      duration: 1.15,
      ease: [0.76, 0, 0.24, 1],
    },
  }),
};

const rise = {
  hidden: { opacity: 0, y: 24 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 1.05 + i * 0.12, duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const overlayY = useTransform(scrollYProgress, [0, 1], ["0%", "-8%"]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid={TID.hero.root}
      className="relative isolate min-h-[100svh] w-full overflow-hidden bg-paper text-navy"
    >
      {/* Light backdrop — subtle violet/gold aura on white, matching the logo */}
      <motion.div
        style={{ y: bgY }}
        aria-hidden
        className="absolute inset-0 -z-10"
      >
        <div className="absolute inset-0 bg-paper" />
        <div className="absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full bg-[color:var(--violet)]/15 blur-[140px]" />
        <div className="absolute -bottom-40 -left-40 h-[520px] w-[520px] rounded-full bg-[color:var(--gold)]/12 blur-[140px]" />
        <div className="absolute top-1/2 left-1/3 h-[360px] w-[360px] rounded-full bg-[#8B5CF6]/10 blur-[120px]" />
      </motion.div>

      {/* Vertical ruler grid — light */}
      <div className="absolute inset-0 rule-grid opacity-40 z-[1]" aria-hidden />

      {/* Top meta */}
      <div className="relative z-10 mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 pt-28 md:pt-32">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.9 }}
          className="flex flex-wrap items-center justify-end gap-4 pb-10 md:pb-16 border-b border-navy/10"
        >
          <div data-testid={TID.hero.dpiit}>
            <DpiitBadge variant="light" />
          </div>
        </motion.div>
      </div>

      {/* Headline */}
      <motion.div
        style={{ y: overlayY }}
        className="relative z-10 mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 pt-10 md:pt-20 pb-16"
      >
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 lg:col-span-9">
            <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-[color:var(--violet)]">
              <span className="text-navy/50">01 —</span> The MSMEmoney doctrine
            </p>
            <h1
              data-testid={TID.hero.title}
              className="mt-6 font-serif tracking-tight text-[10.5vw] md:text-[7.2vw] lg:text-[6.2vw] leading-[0.94] text-navy"
            >
              <span className="reveal-mask">
                <motion.span custom={0} variants={line} initial="hidden" animate="show" className="block">
                  India&rsquo;s trusted partner
                </motion.span>
              </span>
              <span className="reveal-mask">
                <motion.span custom={1} variants={line} initial="hidden" animate="show" className="block">
                  for MSME <span className="italic text-[color:var(--violet)]">subsidies</span>,
                </motion.span>
              </span>
              <span className="reveal-mask">
                <motion.span custom={2} variants={line} initial="hidden" animate="show" className="block">
                  debt recovery &amp; project funding.
                </motion.span>
              </span>
            </h1>
          </div>

          <div className="col-span-12 lg:col-span-3 lg:pl-6 lg:border-l lg:border-navy/10 flex flex-col justify-end">
            <motion.p
              custom={0}
              variants={rise}
              initial="hidden"
              animate="show"
              data-testid={TID.hero.subtitle}
              className="text-navy/70 text-base md:text-[17px] leading-relaxed max-w-sm"
            >
              We help Micro, Small &amp; Medium Enterprises unlock government
              incentives, recover what they&rsquo;re owed, and fund what they&rsquo;re building —
              end-to-end, paperwork to disbursement.
            </motion.p>
          </div>
        </div>

        {/* CTAs */}
        <motion.div
          custom={1}
          variants={rise}
          initial="hidden"
          animate="show"
          className="mt-12 md:mt-16 flex flex-wrap items-center gap-4"
        >
          <a
            href="#contact"
            data-testid={TID.hero.ctaPrimary}
            className="group inline-flex"
          >
            <ShimmerButton
              className="!bg-[color:var(--gold)] hover:!bg-navy hover:!text-white transition-colors"
              shimmerDuration="2.4s"
            >
              <span className="font-mono text-[11px] uppercase tracking-[0.24em] z-10 relative">
                Talk to an Expert
              </span>
              <ArrowUpRight className="ml-3 h-4 w-4 relative z-10 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </ShimmerButton>
          </a>
          <a
            href="#services"
            data-testid={TID.hero.ctaSecondary}
            className="group inline-flex items-center gap-3 border border-navy/25 px-6 py-4 text-navy hover:border-navy hover:bg-navy hover:text-white transition-colors"
          >
            <span className="font-mono text-[11px] uppercase tracking-[0.24em]">
              What we do
            </span>
            <ArrowDown className="h-4 w-4 transition-transform group-hover:translate-y-0.5" />
          </a>
        </motion.div>

        {/* Scroll cue */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.9, duration: 1 }}
          className="mt-16 md:mt-24 flex items-center gap-4"
        >
          <span className="h-px w-20 bg-navy/25" />
          <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-navy/45">
            Scroll · chapters 02 — 09
          </span>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
